import {useCallback, useState} from "react";
import {IOptionType, partPositionDatabaseNamespaceValue} from "../types/common";
import {Amplify} from "aws-amplify";
import aws_config from "../modified_aws-export";
import {selectedItemGroupsKey, selectedManufacturersKey,} from "../utils/imageMatcherUtil";
import {queryItemFromPinecone} from "../services/imageMatcher/queryItemFromPinecone";
import {buildImageMatcherFilter} from "../utils/modelBuilder";
import {useRecordEvent} from "aws-rum-react";
import {useConfigState} from "./useConfig";
import {useUploadedImages} from "./useUploadedImages";
import {IMatchModel} from "../types/pinecone";
import {downloadImageAndConvertToFile} from "../utils/storageUtil";
import {IImageMatcherFilterModel} from "../types/imageMatcher";
import {useConfirmNavigation} from "./useConfirmNavigation";

Amplify.configure(aws_config);

export const useImageMatcherFilter = () => {
  const recordEvent = useRecordEvent();
  const { top_k } = useConfigState();
  const { getImageById } = useUploadedImages();
  const [selectedItemGroups, setSelectedItemGroups] = useState<IOptionType[]>(
    []
  );
  const [selectedManufacturers, setSelectedManufacturers] = useState<
    IOptionType[]
  >([]);
  const { databaseNamespaces } = useConfigState();
  const [selectedNameSpace, setSelectedNameSpace] = useState<string>(databaseNamespaces.value === "BTL" ? "BtlAll" : "All");
  const [metadata, setMetadata] = useState<Record<string, { $in: string[] }>>();
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [imageFileObject, setImageFileObject] = useState<File | null>();
  const [pineconeMatches, setPineconeMatches] = useState<IMatchModel[]>([]);
  const {
    isModalOpen,
    confirmNavigation,
    cancelNavigation,
    setIsDirty
  } = useConfirmNavigation();

  const handleFetchFilterResults = useCallback(
    async (imageId: string, filterProps: IImageMatcherFilterModel) => {
      if (!imageId) return;
      setIsLoading(true);
      try {
        const isUsingAim =
            filterProps.selectedNameSpace === "AimModel" ||
            filterProps.selectedNameSpace === "BtlSingle";
        const databaseNamespace =
          filterProps.selectedNameSpace === "BtlAll" || 
          filterProps.selectedNameSpace === "BtlSingle"
          ? partPositionDatabaseNamespaceValue
          : null;

        const pineconeQueryResult = await queryItemFromPinecone({
          dropFileId: imageId,
          key: `image_similarity_search/${imageId}.png`,
          topK: Number(top_k),
          filter: buildImageMatcherFilter(filterProps),
          isUsingAim: isUsingAim,
          namespace: databaseNamespace,
        });
        recordEvent(pineconeQueryResult.trackingModel, {
          duration: `${(pineconeQueryResult.duration / 1000).toFixed(2)}s`,
        });
        return pineconeQueryResult;
      } finally {
        setIsLoading(false);
      }
    },
    [
      top_k,
      selectedItemGroups,
      selectedManufacturers,
      selectedNameSpace,
      metadata,
      recordEvent,
    ]
  );

  const getFilterFromStorage = async () => {
    try {
      const manufacturersStorage = localStorage.getItem(
        selectedManufacturersKey
      );
      const itemGroupsStorage = localStorage.getItem(selectedItemGroupsKey);

      const selectedManufacturers = manufacturersStorage
        ? (JSON.parse(manufacturersStorage) as IOptionType[])
        : [];
      const selectedItemGroups = itemGroupsStorage
        ? (JSON.parse(itemGroupsStorage) as IOptionType[])
        : [];

      setSelectedManufacturers(selectedManufacturers);
      setSelectedItemGroups(selectedItemGroups);

      return { selectedManufacturers, selectedItemGroups };
    } catch (error) {
      console.error("Error retrieving filters from storage:", error);
      return { selectedManufacturers: [], selectedItemGroups: [] };
    }
  };

  const getCurrentUploadedImage = async (imageId: string) => {
    try {
      const { selectedManufacturers, selectedItemGroups } =
        await getFilterFromStorage();

      const currentImage = getImageById(imageId);
      if (!currentImage) return;

      setPreviewImage(currentImage.preview);

      const [fileObject, pineconeResults] = await Promise.all([
        downloadImageAndConvertToFile(imageId),
        handleFetchFilterResults(imageId, {
          selectedItemGroups,
          selectedManufacturers,
          selectedNameSpace,
          metadata,
        }),
      ]);

      setImageFileObject(fileObject);
      setPineconeMatches(pineconeResults?.results ?? []);
      setIsDirty(true);
    } catch (error) {
      console.error(`Error fetching uploaded image ${imageId}:`, error);
    }
  };

  const handleApplyFilter = async (imageId: string) => {
    const pineconeResults = await handleFetchFilterResults(imageId, {
      selectedItemGroups,
      selectedManufacturers,
      selectedNameSpace,
      metadata,
    });
    setPineconeMatches(pineconeResults?.results ?? []);
  };

  return {
    selectedItemGroups,
    setSelectedItemGroups,
    selectedManufacturers,
    setSelectedManufacturers,
    selectedNameSpace,
    setSelectedNameSpace,
    metadata,
    setMetadata,
    handleFetchFilterResults,
    isLoading,
    previewImage,
    imageFileObject,
    pineconeMatches,
    getCurrentUploadedImage,
    getFilterFromStorage,
    setPineconeMatches,
    handleApplyFilter,
    isModalOpen,
    confirmNavigation,
    cancelNavigation
  };
};
